import React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n/I18nContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LaunchIcon from '@mui/icons-material/Launch';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';

const OpenAISetupPage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useI18n();

	return (
		<Container maxWidth="md" sx={{ py: 4 }}>
			<Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
				<IconButton onClick={() => navigate('/dashboard')}>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant="h4" fontWeight={800}>
					{t.openaiSetup.title}
				</Typography>
			</Box>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
					{t.openaiSetup.introduction}
				</Typography>
				<Typography variant="body1" color="text.secondary" paragraph>
					{t.openaiSetup.introductionText}
				</Typography>
			</Paper>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
					<AccountCircleIcon color="primary" />
					<Typography variant="h6" fontWeight={700}>
						{t.openaiSetup.step1Title}
					</Typography>
				</Box>
				<Typography variant="body1" color="text.secondary" paragraph>
					{t.openaiSetup.step1Description}
				</Typography>
				<Box sx={{ mt: 2, mb: 2 }}>
					<Link href="https://platform.openai.com/signup" target="_blank" rel="noopener noreferrer">
						<Button variant="contained" startIcon={<AccountCircleIcon />} endIcon={<LaunchIcon />}>
							{t.openaiSetup.createOpenAIAccount}
						</Button>
					</Link>
				</Box>
			</Paper>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
					<VpnKeyIcon color="primary" />
					<Typography variant="h6" fontWeight={700}>
						{t.openaiSetup.step2Title}
					</Typography>
				</Box>
				<Typography variant="body1" color="text.secondary" paragraph>
					{t.openaiSetup.step2Description}
				</Typography>
				<Box sx={{ mt: 2, mb: 2 }}>
					<Link href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
						<Button variant="contained" startIcon={<VpnKeyIcon />} endIcon={<LaunchIcon />}>
							{t.openaiSetup.openAPIKeysPage}
						</Button>
					</Link>
				</Box>
				<Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mt: 2 }}>
					<Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', mb: 1 }}>
						{t.openaiSetup.apiKeyLocation}
					</Typography>
				</Box>
			</Paper>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
					<CheckCircleIcon color="primary" />
					<Typography variant="h6" fontWeight={700}>
						{t.openaiSetup.step3Title}
					</Typography>
				</Box>
				<Typography variant="body1" color="text.secondary" paragraph>
					{t.openaiSetup.step3Description}
				</Typography>
				<Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mt: 2 }}>
					<Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
						{t.openaiSetup.apiKeyFormat}
					</Typography>
				</Box>
			</Paper>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
					{t.openaiSetup.step4Title}
				</Typography>
				<Typography variant="body1" color="text.secondary" paragraph>
					{t.openaiSetup.step4Description}
				</Typography>
				<List>
					<ListItem>
						<ListItemIcon>
							<CheckCircleIcon color="success" />
						</ListItemIcon>
						<ListItemText primary={t.openaiSetup.checklist1} />
					</ListItem>
					<ListItem>
						<ListItemIcon>
							<CheckCircleIcon color="success" />
						</ListItemIcon>
						<ListItemText primary={t.openaiSetup.checklist2} />
					</ListItem>
					<ListItem>
						<ListItemIcon>
							<CheckCircleIcon color="success" />
						</ListItemIcon>
						<ListItemText primary={t.openaiSetup.checklist3} />
					</ListItem>
				</List>
			</Paper>

			<Paper sx={{ p: 4, mb: 3 }}>
				<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
					{t.openaiSetup.importantNotes}
				</Typography>
				<Stack spacing={2}>
					<Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
							<SecurityIcon color="warning" />
							<Typography variant="subtitle2" fontWeight={700} color="warning.main">
								{t.openaiSetup.note1Title}
							</Typography>
						</Box>
						<Typography variant="body2" color="text.secondary">
							{t.openaiSetup.note1Text}
						</Typography>
					</Box>
					<Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
							<SecurityIcon color="warning" />
							<Typography variant="subtitle2" fontWeight={700} color="warning.main">
								{t.openaiSetup.note2Title}
							</Typography>
						</Box>
						<Typography variant="body2" color="text.secondary">
							{t.openaiSetup.note2Text}
						</Typography>
					</Box>
					<Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
							<InfoIcon color="info" />
							<Typography variant="subtitle2" fontWeight={700} color="info.main">
								{t.openaiSetup.note3Title}
							</Typography>
						</Box>
						<Typography variant="body2" color="text.secondary">
							{t.openaiSetup.note3Text}
						</Typography>
					</Box>
					<Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
							<InfoIcon color="info" />
							<Typography variant="subtitle2" fontWeight={700} color="info.main">
								{t.openaiSetup.note4Title}
							</Typography>
						</Box>
						<Typography variant="body2" color="text.secondary">
							{t.openaiSetup.note4Text}
						</Typography>
					</Box>
				</Stack>
			</Paper>

			<Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
				<Button variant="outlined" onClick={() => navigate('/dashboard')}>
					{t.common.cancel}
				</Button>
				<Button variant="contained" onClick={() => navigate('/dashboard')}>
					{t.openaiSetup.goToDashboard}
				</Button>
			</Box>
		</Container>
	);
};

export default OpenAISetupPage;
